/**
 * Create task form page — CoordioCore form pattern.
 *
 * Uses PageFormLayout with left sidebar navigation and FormSection cards,
 * mirroring ExternalTaskSystemForm.tsx as the reference implementation.
 *
 * Fields map 1:1 to the `CreateExternalTaskRequest` DTO in hitlService.ts:
 *  - externalTaskSystemId  (required — dropdown loaded from the systems list)
 *  - taskType              (required — Approval | Review | Action | Information)
 *  - title                 (required)
 *  - description           (optional)
 *  - assignedTo            (optional)
 *  - dueDate               (optional)
 *  - contextJson           (optional — raw JSON for agent context)
 *  - agentSessionId        (optional — GUID deep-link)
 *  - safetyGateExecutionId (optional — GUID deep-link)
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Link2, Settings2 } from 'lucide-react';

import {
  CoordioDropdown,
  FormField,
  Input,
  Textarea,
} from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../components/layouts/PageFormLayout';
import {
  hitlService,
  TaskType,
  type CreateExternalTaskRequest,
  type ExternalTaskSystemDto,
} from '../services/hitlService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

const TASK_TYPE_OPTIONS = Object.values(TaskType).map((t) => ({
  label: t,
  value: t,
}));

/**
 * Task create form — route: /tasks/new
 */
export const TaskFormPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ── form fields ── */
  const [externalTaskSystemId, setExternalTaskSystemId] = useState('');
  const [taskType, setTaskType] = useState<string>('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [contextJson, setContextJson] = useState('');
  const [agentSessionId, setAgentSessionId] = useState('');
  const [safetyGateExecutionId, setSafetyGateExecutionId] = useState('');

  /* ── external task systems dropdown options ── */
  const [systems, setSystems] = useState<ExternalTaskSystemDto[]>([]);
  const [loadingSystems, setLoadingSystems] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const result = await hitlService.getExternalTaskSystems({ pageSize: 200, isActive: true });
        setSystems(result.items ?? []);
      } catch {
        addNotification('Failed to load external task systems', 'error');
      } finally {
        setLoadingSystems(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const systemOptions = systems.map((s) => ({
    label: `${s.name} (${s.systemType})`,
    value: s.id,
  }));

  /* ── submit ── */
  const handleSubmit = async () => {
    setError(null);

    if (!externalTaskSystemId) {
      setError('External task system is required.');
      return;
    }
    if (!taskType) {
      setError('Task type is required.');
      return;
    }
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    if (contextJson.trim()) {
      try {
        JSON.parse(contextJson.trim());
      } catch {
        setError('Context JSON must be valid JSON.');
        return;
      }
    }

    setSaving(true);
    try {
      const request: CreateExternalTaskRequest = {
        externalTaskSystemId,
        taskType: taskType as typeof TaskType[keyof typeof TaskType],
        title: title.trim(),
        description: description.trim() || undefined,
        assignedTo: assignedTo.trim() || undefined,
        dueDate: dueDate || undefined,
        contextJson: contextJson.trim() || undefined,
        agentSessionId: agentSessionId.trim() || undefined,
        safetyGateExecutionId: safetyGateExecutionId.trim() || undefined,
      };

      const created = await hitlService.createTask(request);
      addNotification('Task created successfully', 'success');
      navigate(`/tasks/${created.id}`);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to create task';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title="Create Task"
      subtitle="Create a new human-in-the-loop task for review or approval."
      data-testid="task-form-page"
      loading={false}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to Tasks', href: ROUTES.TASKS }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate(ROUTES.TASKS),
        saveLabel: 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'Task',
          items: [
            { id: 'details', label: 'Details', icon: <ClipboardList size={16} /> },
            { id: 'routing', label: 'Routing', icon: <Link2 size={16} /> },
          ],
        },
        {
          title: 'Advanced',
          items: [
            { id: 'advanced', label: 'Advanced', icon: <Settings2 size={16} /> },
          ],
        },
      ]}
      sections={[
        /* ── Section 1: Details ── */
        {
          id: 'details',
          title: 'Details',
          icon: <ClipboardList size={20} />,
          content: (
            <FormSection
              title="Task identity"
              description="Provide a title, select the task type, and optionally describe what this task requires."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Title"
                  required
                  description="Short summary of what the task requires."
                  data-testid="task-title"
                >
                  <Input
                    className="w-full"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Approve refund override"
                    data-testid="task-title"
                  />
                </FormField>

                <FormField
                  label="Task Type"
                  required
                  description="Categorises how this task should be handled."
                  data-testid="task-taskType"
                >
                  <CoordioDropdown
                    options={TASK_TYPE_OPTIONS}
                    value={taskType || null}
                    onChange={(v) => setTaskType(v ?? '')}
                    clearable={false}
                    data-testid="task-taskType"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Description"
                  description="Optional detailed description of the task."
                  data-testid="task-description"
                >
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    placeholder="Describe what needs to be reviewed or approved…"
                    data-testid="task-description"
                  />
                </FormField>
              </div>

              <div className="grid gap-5 items-end sm:grid-cols-2 mt-5">
                <FormField
                  label="Assigned To"
                  description="User ID to assign this task to (optional)."
                  data-testid="task-assignedTo"
                >
                  <Input
                    className="w-full"
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                    placeholder="User ID"
                    data-testid="task-assignedTo"
                  />
                </FormField>

                <FormField
                  label="Due Date"
                  description="Optional deadline for this task."
                  data-testid="task-dueDate"
                >
                  <Input
                    className="w-full"
                    type="datetime-local"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    data-testid="task-dueDate"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },

        /* ── Section 2: Routing ── */
        {
          id: 'routing',
          title: 'Routing',
          icon: <Link2 size={20} />,
          content: (
            <FormSection
              title="Task routing"
              description="Choose the external system (Jira, ServiceNow, etc.) where this task will be tracked."
            >
              <FormField
                label="External Task System"
                required
                description="The system this task belongs to."
                data-testid="task-externalTaskSystemId"
              >
                <CoordioDropdown
                  options={systemOptions}
                  value={externalTaskSystemId || null}
                  onChange={(v) => setExternalTaskSystemId(v ?? '')}
                  clearable={false}
                  placeholder={loadingSystems ? 'Loading systems…' : 'Select a system'}
                  disabled={loadingSystems}
                  data-testid="task-externalTaskSystemId"
                />
              </FormField>
            </FormSection>
          ),
        },

        /* ── Section 3: Advanced ── */
        {
          id: 'advanced',
          title: 'Advanced',
          icon: <Settings2 size={20} />,
          content: (
            <FormSection
              title="Advanced options"
              description="Optional linking to agent sessions, safety gate executions, and raw context data."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Agent Session ID"
                  description="Link this task to an existing agent session (GUID)."
                  data-testid="task-agentSessionId"
                >
                  <Input
                    className="w-full"
                    value={agentSessionId}
                    onChange={(e) => setAgentSessionId(e.target.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    data-testid="task-agentSessionId"
                  />
                </FormField>

                <FormField
                  label="Safety Gate Execution ID"
                  description="Link this task to a safety gate execution (GUID)."
                  data-testid="task-safetyGateExecutionId"
                >
                  <Input
                    className="w-full"
                    value={safetyGateExecutionId}
                    onChange={(e) => setSafetyGateExecutionId(e.target.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    data-testid="task-safetyGateExecutionId"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Context JSON"
                  description="Optional JSON payload with agent context for this task."
                  data-testid="task-contextJson"
                >
                  <Textarea
                    className="w-full font-mono text-[13px]"
                    value={contextJson}
                    onChange={(e) => setContextJson(e.target.value)}
                    rows={6}
                    placeholder={'{ "key": "value" }'}
                    data-testid="task-contextJson"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
